<template>
  <OurPage :back-route="backRoute" :title="$t('promotions.dealersList.title')" gray>
    <template #header-right>
      <OurButton
        variant="thirdary"
        filled
        :text="$t('promotions.dealersList.exportXlsx')"
        :disabled="isExporting || !promotionId"
        @click="onExportXlsx"
      />
    </template>

    <p class="mb-4 text-sm text-gray-500">{{ $t("promotions.dealersList.description") }}</p>

    <PromotionRankingBanner v-if="promotion" class="mb-6" :promotion="promotion" />

    <OurEmptyBlock
      v-if="isPlannedEmpty"
      class="mb-6"
      :description="$t('promotions.dealersList.emptyPlanned')"
    />

    <DealersRankingList
      :items="rankingItems"
      :pagination-data="paginationData"
      :order-by="orderBy"
      :per-page="perPage"
      :sort-type="sortType"
      @sort="onSort"
      @pageChange="onPageChange"
      @perPageChange="onPerPageChange"
      @sortTypeChange="onSortTypeChange"
      @clickDealer="onClickDealer"
    />
  </OurPage>
</template>

<script>
import apolloMixin from "@/mixins/apollo.mixin";
import dealerIncentivesApi from "@/api/dealerIncentives";
import PromotionRankingBanner from "./_components/PromotionRankingBanner.vue";
import DealersRankingList from "./_components/DealersRankingList.vue";
import { dealerIncentiveBannerQuery, dealerIncentiveRankingQuery } from "./gql/queries";

const DEFAULT_SORTING = { column: "place", direction: "asc" };
const DEFAULT_SORT_TYPE = "place_asc";

const SORTABLE_COLUMNS = [
  "place",
  "customer_name",
  "dealer_level",
  "incentive_level",
  "shipments_sum",
];

const SORT_OPTIONS = [
  { id: "place_desc", sortable: "-place" },
  { id: "place_asc", sortable: "place" },
  { id: "customer_name_asc", sortable: "customer_name" },
  { id: "customer_name_desc", sortable: "-customer_name" },
  { id: "dealer_level_asc", sortable: "dealer_level" },
  { id: "dealer_level_desc", sortable: "-dealer_level" },
  { id: "incentive_level_asc", sortable: "incentive_level" },
  { id: "incentive_level_desc", sortable: "-incentive_level" },
  { id: "shipments_sum_desc", sortable: "-shipments_sum" },
  { id: "shipments_sum_asc", sortable: "shipments_sum" },
];

const parseSortingFromQuery = (sortParam) => {
  if (!sortParam) {
    return DEFAULT_SORTING;
  }

  const direction = sortParam.startsWith("-") ? "desc" : "asc";
  const column = direction === "desc" ? sortParam.slice(1) : sortParam;

  if (!SORTABLE_COLUMNS.includes(column)) {
    return DEFAULT_SORTING;
  }

  return { column, direction };
};

const sortingToQuery = ({ column, direction }) => {
  return direction === "desc" ? `-${column}` : column;
};

export default {
  name: "PromotionsRanking",

  components: {
    PromotionRankingBanner,
    DealersRankingList,
  },

  mixins: [apolloMixin],

  data() {
    return {
      backRoute: {
        name: "Promotions",
        title: this.$t("title.promotions._"),
      },
      promotion: null,
      rankingItems: [],
      paginationData: {},
      orderBy: DEFAULT_SORTING,
      sortType: DEFAULT_SORT_TYPE,
      perPage: 10,
      isExporting: false,
    };
  },

  computed: {
    promotionId() {
      return this.$route.params.id;
    },

    isPlannedEmpty() {
      const status = this.promotion?.status?.toLowerCase?.() || this.promotion?.status;

      return status === "planned" && !this.rankingItems.length;
    },
  },

  watch: {
    "$route.query.sort"(sort) {
      if (sortingToQuery(this.orderBy) === sort) {
        return;
      }

      this.orderBy = parseSortingFromQuery(sort);
      this.syncSortTypeWithOrderBy();
      this.fetchRanking();
    },

    "$route.params.id"() {
      this.resetAndLoad();
    },
  },

  created() {
    this.orderBy = parseSortingFromQuery(this.$route.query.sort);
    this.syncSortTypeWithOrderBy();
    this.resetAndLoad();
  },

  methods: {
    resetAndLoad() {
      this.perPage = 10;
      this.syncSortToUrl().then(() => {
        this.fetchBanner();
        this.fetchRanking(parseInt(this.$route.query.page, 10) || 1);
      });
    },

    buildListQuery({ sort, page } = {}) {
      const query = { ...this.$route.query };
      const nextSort = sort ?? sortingToQuery(this.orderBy);

      if (nextSort) {
        query.sort = nextSort;
      }

      if (page === 1) {
        delete query.page;
      } else if (page) {
        query.page = page;
      }

      return query;
    },

    replaceListRoute(queryOverrides = {}) {
      const query = this.buildListQuery(queryOverrides);
      const isSameRoute =
        query.sort === this.$route.query.sort &&
        String(query.page || "") === String(this.$route.query.page || "");

      if (isSameRoute) {
        return Promise.resolve();
      }

      return this.$router.replace({
        name: this.$route.name,
        params: this.$route.params,
        query,
      });
    },

    syncSortToUrl() {
      const sort = sortingToQuery(this.orderBy);

      if (sort === this.$route.query.sort) {
        return Promise.resolve();
      }

      return this.replaceListRoute({ sort });
    },

    syncSortTypeWithOrderBy() {
      const sort = sortingToQuery(this.orderBy);
      const option = SORT_OPTIONS.find((item) => item.sortable === sort);

      this.sortType = option?.id ?? DEFAULT_SORT_TYPE;
    },

    async fetchBanner() {
      const { dealerIncentive } = await this.$get(dealerIncentiveBannerQuery, {
        id: this.promotionId,
      });

      this.promotion = dealerIncentive || null;
    },

    async fetchRanking(page = parseInt(this.$route.query.page, 10) || 1) {
      const { dealerIncentiveRanking } = await this.$get(dealerIncentiveRankingQuery, {
        dealerIncentiveId: this.promotionId,
        page,
        first: this.perPage,
        orderBy: this.orderBy,
      });

      if (!dealerIncentiveRanking) {
        this.rankingItems = [];
        this.paginationData = {};

        return;
      }

      this.rankingItems = (dealerIncentiveRanking.data || []).map((row) => this.prepareRow(row));
      this.paginationData = dealerIncentiveRanking.paginatorInfo || {};
    },

    prepareRow(row) {
      const customer = row.customer || {};
      const progress = row.progress || {};

      return {
        place: row.place,
        customerId: customer.id,
        customerName: customer.name || "–",
        dealer: {
          id: customer.id,
          name: customer.name || "–",
        },
        dealerLevel: customer.bonusProgramLevel?.name || "",
        incentiveLevel: {
          level: progress.level ?? 1,
          progressPercent: progress.progressPercent ?? 0,
        },
        shipmentsSum: progress.shipmentsSum ?? 0,
        progressPercent: progress.progressPercent ?? 0,
        incentiveLevelValue: progress.level ?? 1,
      };
    },

    async onSort(header) {
      const column = header.sortable;

      if (!column || !SORTABLE_COLUMNS.includes(column)) {
        return;
      }

      let direction = column === "place" ? "asc" : "desc";

      if (this.orderBy.column === column) {
        direction = this.orderBy.direction === "asc" ? "desc" : "asc";
      }

      this.orderBy = { column, direction };
      this.syncSortTypeWithOrderBy();
      await this.replaceListRoute({ sort: sortingToQuery(this.orderBy), page: 1 });
      this.fetchRanking(1);
    },

    async onSortTypeChange(sortTypeId) {
      const option = SORT_OPTIONS.find((item) => item.id === sortTypeId);

      if (!option) {
        return;
      }

      const nextOrderBy = parseSortingFromQuery(option.sortable);

      if (
        nextOrderBy.column === this.orderBy.column &&
        nextOrderBy.direction === this.orderBy.direction
      ) {
        return;
      }

      this.orderBy = nextOrderBy;
      this.sortType = sortTypeId;
      await this.replaceListRoute({ sort: sortingToQuery(this.orderBy), page: 1 });
      this.fetchRanking(1);
    },

    onPageChange(page) {
      this.fetchRanking(page);
    },

    async onPerPageChange(value) {
      this.perPage = value;
      await this.replaceListRoute({ page: 1 });
      this.fetchRanking(1);
    },

    onClickDealer(customerId) {
      if (!customerId) {
        return;
      }

      this.$router.push({ name: "CustomersEdit", params: { id: customerId } });
    },

    async onExportXlsx() {
      if (!this.promotionId || this.isExporting) {
        return;
      }

      this.isExporting = true;

      try {
        const blob = await dealerIncentivesApi.downloadRankingXlsx(this.promotionId);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = `dealer-incentive-ranking-${this.promotionId}.xlsx`;
        link.click();
        window.URL.revokeObjectURL(url);
      } finally {
        this.isExporting = false;
      }
    },
  },
};
</script>
